import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Edit, MapPin, Phone, Mail, Globe, Building, Shield, Users, FileText } from 'lucide-react';
import { getCustomer } from '../../config/api';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => getCustomer(id)
  });

  if (isLoading) return <div className="flex justify-center p-8">Loading customer details...</div>;
  if (error) return <div className="text-red-500 p-8">Error loading customer: {error.message}</div>;
  if (!customer) return <div className="text-red-500 p-8">Customer not found</div>;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/app/customers')}
          className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100"
          title="Back to Customers"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
          <p className="text-gray-600">{customer.classOfTrade || customer.type || ''}</p>
        </div>
        <button
          onClick={() => navigate(`/app/customers/${id}/edit`)}
          className="ml-auto bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-700"
          title="Edit Customer"
        >
          <Edit size={16} />
          Edit Customer
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building size={20} />
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Customer Type</label>
                <p className="text-sm text-gray-900">{customer.type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  customer.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {customer.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">EDI Entity ID</label>
                <p className="text-sm text-gray-900">{customer.ediEntityId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Warehouse ID</label>
                <p className="text-sm text-gray-900">{customer.defaultWarehouse?.name || customer.defaultWarehouseId || '-'}</p>
              </div>
            </div>
          </div>

          {/* Hierarchy Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={20} />
              Hierarchy & Classification
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Level</label>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    customer.level === 0 ? 'bg-purple-100 text-purple-800' :
                    customer.level === 1 ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {customer.level === 0 ? 'Parent' : customer.level === 1 ? 'Child' : 'Grandchild'}
                  </span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Parent Customer</label>
                <p className="text-sm text-gray-900">
                  {customer.parent ? customer.parent.name : 'None (Top Level)'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Child Accounts</label>
                <p className="text-sm text-gray-900">
                  {customer.children && customer.children.length > 0
                    ? customer.children.map((c) => c.name).join(', ')
                    : 'None'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Class of Trade</label>
                <p className="text-sm text-gray-900">{customer.classOfTrade || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Contract Group</label>
                <p className="text-sm text-gray-900">{customer.contractGroup || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Specialty</label>
                <p className="text-sm text-gray-900">{customer.specialty || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Tier</label>
                <p className="text-sm text-gray-900">{customer.tier || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Territory</label>
                <p className="text-sm text-gray-900">{customer.territory || '-'}</p>
              </div>
            </div>
          </div>

          {/* Regulatory Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield size={20} />
              Regulatory & Compliance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">DEA Number</label>
                <p className="text-sm text-gray-900">{customer.deaNumber || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">NPI Number</label>
                <p className="text-sm text-gray-900">{customer.npiNumber || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Tax ID</label>
                <p className="text-sm text-gray-900">{customer.taxId || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">State License</label>
                <p className="text-sm text-gray-900">{customer.stateLicense || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">FDA Registration</label>
                <p className="text-sm text-gray-900">{customer.fdaRegistration || '-'}</p>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText size={20} />
              Financial & GL Codes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Credit Limit</label>
                <p className="text-sm text-gray-900">
                  {customer.creditLimit ? `$${Number(customer.creditLimit).toLocaleString()}` : '-'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Payment Terms</label>
                <p className="text-sm text-gray-900">{customer.paymentTerms || '-'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone size={20} />
              Contact Information
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone size={16} />
                <span>{customer.phoneNumber || 'No phone'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail size={16} />
                <span>{customer.emailAddress || 'No email'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Globe size={16} />
                <span>{customer.website || 'No website'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin size={16} />
                <span>{customer.primaryContact || 'No primary contact'}</span>
              </div>
            </div>
          </div>

          {/* EDI Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">EDI Settings</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">EDI 845 Enabled</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  customer.edi845Enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {customer.edi845Enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">EDI 844 Enabled</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  customer.edi844Enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {customer.edi844Enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">EDI 849 Enabled</span>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  customer.edi849Enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {customer.edi849Enabled ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Contracts</span>
                <span className="text-sm font-medium text-gray-900">{customer._count?.contractCustomers || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Chargebacks</span>
                <span className="text-sm font-medium text-gray-900">{customer._count?.chargebackClaims || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Child Accounts</span>
                <span className="text-sm font-medium text-gray-900">{customer._count?.children || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
